'use client';

import { useEffect, useRef } from 'react';
import {
  DayPicker,
  SelectionState,
  UI,
  useDayPicker,
  type DayButtonProps,
  type DayPickerProps,
  type MonthCaptionProps,
} from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { calendarStyles, dayButtonStyles, navButtonStyles } from './calendar.styles';

import { Icon } from '@/components/ui/icon.component';
import { cn } from '@/utils/cn-tw-merge';

const styles = calendarStyles();

export type CalendarProps = DayPickerProps & {
  dataTestId?: string;
};

function CalendarNavButton({
  direction,
  disabled,
  onClick,
  dataTestId,
}: {
  direction: 'previous' | 'next';
  disabled: boolean;
  onClick: () => void;
  dataTestId: string;
}) {
  const { button, circle, icon } = navButtonStyles({ disabled });

  return (
    <button type="button" className={button()} disabled={disabled} onClick={onClick} data-testid={dataTestId}>
      <span className={circle()}>
        <Icon icon={direction === 'previous' ? faArrowLeft : faArrowRight} className={icon()} aria-hidden />
      </span>
    </button>
  );
}

function CalendarCaption({ calendarMonth, displayIndex: _displayIndex, className, ...props }: MonthCaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  const label = format(calendarMonth.date, 'LLLL yyyy', { locale: ptBR });

  return (
    <div className={cn(styles.monthCaption(), className)} {...props}>
      <CalendarNavButton
        direction="previous"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        dataTestId={DataTestIdEnum.CALENDAR_PREVIOUS_MONTH_BUTTON}
      />
      <span className={styles.captionLabel()}>{label}</span>
      <CalendarNavButton
        direction="next"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        dataTestId={DataTestIdEnum.CALENDAR_NEXT_MONTH_BUTTON}
      />
    </div>
  );
}

function CalendarDayButton({ day, modifiers, className: _className, children: _children, ...props }: DayButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  const isFilled = modifiers.selected && !modifiers.range_middle;

  const className = dayButtonStyles({
    selected: isFilled,
    today: modifiers.today && !isFilled,
    outside: modifiers.outside,
    disabled: modifiers.disabled,
  });

  return (
    <button ref={ref} className={className} {...props}>
      {format(day.date, 'dd', { locale: ptBR })}
    </button>
  );
}

function formatWeekdayName(date: Date): string {
  const weekday = format(date, 'EEEEEE', { locale: ptBR });
  return weekday.charAt(0).toUpperCase() + weekday.slice(1);
}

export function Calendar({
  className,
  classNames,
  components,
  formatters,
  showOutsideDays = true,
  dataTestId,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      data-testid={dataTestId}
      locale={ptBR}
      showOutsideDays={showOutsideDays}
      hideNavigation
      className={cn(styles.root(), className)}
      classNames={{
        [UI.Months]: styles.months(),
        [UI.Month]: styles.month(),
        [UI.MonthGrid]: styles.monthGrid(),
        [UI.Weekday]: styles.weekday(),
        [UI.Day]: styles.day(),
        [SelectionState.range_start]: styles.dayRangeStart(),
        [SelectionState.range_middle]: styles.dayRangeMiddle(),
        [SelectionState.range_end]: styles.dayRangeEnd(),
        ...classNames,
      }}
      components={{
        MonthCaption: CalendarCaption,
        DayButton: CalendarDayButton,
        ...components,
      }}
      formatters={{ formatWeekdayName, ...formatters }}
      {...props}
    />
  );
}
